import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Plus,
  RefreshCw,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Users,
  UserPlus,
  Activity,
  Globe,
  Mail,
  ChevronDown,
  ChevronUp,
  Search,
  Eye,
  EyeOff,
  Folder,
  Filter,
  MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple bar chart for organic traffic
const TrafficChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <p className="text-zinc-500 text-sm">No data available</p>;
  }

  const maxSessions = Math.max(...data.map(d => d.sessions), 1);

  return (
    <div className="space-y-2">
      {data.map((item) => (
        <div key={item.month} className="flex items-center gap-3">
          <span className="text-zinc-500 text-xs font-mono w-16">{item.month}</span>
          <div className="flex-1 bg-zinc-800 rounded-full h-5 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-600 to-cyan-400 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
              style={{ width: `${Math.max((item.sessions / maxSessions) * 100, 5)}%` }}
            >
              {item.sessions > 0 && (
                <span className="text-xs text-white font-mono">{item.sessions.toLocaleString()}</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Property card showing organic traffic
const PropertyCard = ({ property, connectionId, onFetchData, trafficData, isFetching, autoFetch, autoFetchDelay, onToggleHidden, onUpdateGroups, groups }) => {
  const [showChart, setShowChart] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  const [showDetailedTraffic, setShowDetailedTraffic] = useState(false);
  const [detailedData, setDetailedData] = useState(null);
  const [isLoadingDetailed, setIsLoadingDetailed] = useState(false);
  const [availableFilters, setAvailableFilters] = useState(null);
  const [filters, setFilters] = useState({ source: '', medium: '', country: '', city: '' });

  // Auto-fetch data on mount with optional delay for sequential loading
  useEffect(() => {
    if (autoFetch && !trafficData && !hasFetched) {
      const delay = autoFetchDelay || 0;
      const timer = setTimeout(() => {
        setHasFetched(true);
        onFetchData(property.id);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [autoFetch, trafficData, hasFetched, onFetchData, property.id, autoFetchDelay]);

  // Fetch available filter options
  const fetchFilterOptions = async () => {
    try {
      const url = `${API_URL}/api/analytics/traffic/${connectionId}/${encodeURIComponent(property.id)}/sources`;
      console.log('Fetching filter options from:', url);
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        console.log('Filter options received:', data);
        setAvailableFilters(data);
      } else {
        console.error('Failed to fetch filter options:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  // Fetch detailed traffic data
  const fetchDetailedData = async () => {
    setIsLoadingDetailed(true);
    try {
      const params = new URLSearchParams();
      if (filters.source) params.append('source', filters.source);
      if (filters.medium) params.append('medium', filters.medium);
      if (filters.country) params.append('country', filters.country);
      if (filters.city) params.append('city', filters.city);
      
      const url = `${API_URL}/api/analytics/traffic/${connectionId}/${encodeURIComponent(property.id)}/detailed${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setDetailedData(data);
      } else {
        toast.error('Failed to fetch detailed traffic data');
      }
    } catch (error) {
      console.error('Failed to fetch detailed data:', error);
      toast.error('Failed to fetch detailed traffic data');
    }
    setIsLoadingDetailed(false);
  };

  // Load filter options when detailed view is opened
  useEffect(() => {
    if (showDetailedTraffic && !availableFilters) {
      fetchFilterOptions();
      fetchDetailedData();
    }
  }, [showDetailedTraffic]);

  const getTrendIcon = (direction) => {
    if (direction === 'up') return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (direction === 'down') return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-zinc-400" />;
  };

  const getTrendColor = (direction) => {
    if (direction === 'up') return 'text-green-400';
    if (direction === 'down') return 'text-red-400';
    return 'text-zinc-400';
  };

  const toggleGroup = (groupId) => {
    const currentGroups = property.groups || [];
    const newGroups = currentGroups.includes(groupId)
      ? currentGroups.filter(id => id !== groupId)
      : [...currentGroups, groupId];
    onUpdateGroups(property.id, newGroups);
  };

  const applyFilters = () => {
    fetchDetailedData();
  };

  const clearFilters = () => {
    setFilters({ source: '', medium: '', country: '', city: '' });
    setTimeout(fetchDetailedData, 100);
  };

  // Quick filter for organic only
  const setOrganicFilter = () => {
    setFilters({ source: 'google', medium: 'organic', country: '', city: '' });
    setTimeout(fetchDetailedData, 100);
  };

  return (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Globe className="w-4 h-4 text-blue-400 flex-shrink-0" />
          <span className="text-zinc-200 font-medium text-sm truncate">{property.name}</span>
          {/* Group tags */}
          {property.groups?.length > 0 && groups && (
            <div className="flex gap-1 flex-shrink-0">
              {property.groups.map(gid => {
                const group = groups.find(g => g.id === gid);
                return group ? (
                  <span key={gid} className="w-2 h-2 rounded-full" style={{ backgroundColor: group.color }} title={group.name} />
                ) : null;
              })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Group selector toggle */}
          {groups && groups.length > 0 && (
            <button
              onClick={() => setShowGroupSelector(!showGroupSelector)}
              className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
              title="Assign to groups"
            >
              <Folder className="w-3.5 h-3.5" />
            </button>
          )}
          {/* Hide toggle */}
          <button
            onClick={() => onToggleHidden(property.id, !property.hidden)}
            className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
            title={property.hidden ? "Show property" : "Hide property"}
          >
            {property.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
          {/* Refresh */}
          <button
            onClick={() => onFetchData(property.id)}
            disabled={isFetching}
            className="flex items-center gap-1 px-2 py-1 text-xs font-mono bg-blue-600/20 border border-blue-600/30 rounded text-blue-400 hover:bg-blue-600/30 disabled:opacity-50"
          >
            {isFetching ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
          </button>
        </div>
      </div>

      {/* Group selector dropdown */}
      {showGroupSelector && groups && groups.length > 0 && (
        <div className="mb-3 p-2 bg-zinc-900 rounded border border-zinc-700">
          <p className="text-xs text-zinc-500 mb-2">Assign to groups:</p>
          <div className="flex flex-wrap gap-1">
            {groups.map(group => (
              <button
                key={group.id}
                onClick={() => toggleGroup(group.id)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-mono transition-colors ${
                  property.groups?.includes(group.id)
                    ? "bg-zinc-700 text-white"
                    : "bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800"
                }`}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: group.color }} />
                {group.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {isFetching && !trafficData && (
        <div className="flex items-center gap-2 text-zinc-500 text-xs py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading organic traffic data...
        </div>
      )}

      {trafficData && (
        <>
          {/* Summary Stats - Last 30 Days vs Previous 30 Days */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="bg-zinc-900 rounded p-2 text-center">
              <p className="text-lg font-bold text-white">{(trafficData.last_30_days_sessions || trafficData.this_month_sessions)?.toLocaleString() || 0}</p>
              <p className="text-xs text-zinc-500">Last 30 Days</p>
            </div>
            <div className="bg-zinc-900 rounded p-2 text-center">
              <p className="text-lg font-bold text-zinc-400">{(trafficData.prev_30_days_sessions || trafficData.last_month_sessions)?.toLocaleString() || 0}</p>
              <p className="text-xs text-zinc-500">Previous 30 Days</p>
            </div>
            <div className="bg-zinc-900 rounded p-2 text-center">
              <div className="flex items-center justify-center gap-1">
                {getTrendIcon(trafficData.trend_direction)}
                <span className={`text-lg font-bold ${getTrendColor(trafficData.trend_direction)}`}>
                  {trafficData.trend_percent > 0 ? '+' : ''}{trafficData.trend_percent}%
                </span>
              </div>
              <p className="text-xs text-zinc-500">Change</p>
            </div>
          </div>

          {/* Monthly Chart - shown by default */}
          <div className="border-t border-zinc-700 pt-2">
            <button
              onClick={() => setShowChart(!showChart)}
              className="flex items-center gap-2 text-xs text-zinc-400 hover:text-zinc-300 mb-2"
            >
              {showChart ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showChart ? 'Hide' : 'Show'} Monthly Chart
            </button>
            {showChart && (
              <>
                <TrafficChart data={trafficData.monthly_data || []} />
                <p className="text-zinc-600 text-xs mt-2">
                  Total: {trafficData.total_sessions?.toLocaleString() || 0} organic sessions (6 months)
                </p>
              </>
            )}
          </div>

          {/* Detailed Traffic Analysis */}
          <div className="border-t border-zinc-700 pt-2 mt-2">
            <button
              onClick={() => setShowDetailedTraffic(!showDetailedTraffic)}
              className="flex items-center gap-2 text-xs text-zinc-400 hover:text-zinc-300 mb-2"
            >
              {showDetailedTraffic ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {showDetailedTraffic ? 'Hide' : 'Show'} Detailed Traffic Analysis
            </button>
            
            {showDetailedTraffic && (
              <div className="space-y-3">
                {/* Filters */}
                <div className="bg-zinc-900 rounded p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Filter className="w-3 h-3 text-zinc-500" />
                    <span className="text-xs text-zinc-400 font-mono">Filters</span>
                    <button
                      onClick={setOrganicFilter}
                      className="ml-auto text-xs px-2 py-0.5 bg-green-600/20 text-green-400 rounded hover:bg-green-600/30"
                    >
                      Organic Only
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-zinc-500 block mb-1">Source</label>
                      <select
                        value={filters.source}
                        onChange={(e) => setFilters({...filters, source: e.target.value})}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-300"
                      >
                        <option value="">All Sources</option>
                        {availableFilters?.sources?.map(s => (
                          <option key={s.value} value={s.value}>{s.value} ({s.users})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 block mb-1">Medium</label>
                      <select
                        value={filters.medium}
                        onChange={(e) => setFilters({...filters, medium: e.target.value})}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-300"
                      >
                        <option value="">All Mediums</option>
                        {availableFilters?.mediums?.map(m => (
                          <option key={m.value} value={m.value}>{m.value} ({m.users})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 block mb-1">Country</label>
                      <select
                        value={filters.country}
                        onChange={(e) => setFilters({...filters, country: e.target.value})}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-300"
                      >
                        <option value="">All Countries</option>
                        {availableFilters?.countries?.map(c => (
                          <option key={c.value} value={c.value}>{c.value} ({c.users})</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end gap-1">
                      <button
                        onClick={applyFilters}
                        disabled={isLoadingDetailed}
                        className="flex-1 bg-blue-600 text-white text-xs px-2 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {isLoadingDetailed ? 'Loading...' : 'Apply'}
                      </button>
                      <button
                        onClick={clearFilters}
                        className="px-2 py-1 text-xs text-zinc-400 hover:text-zinc-200"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>

                {isLoadingDetailed && (
                  <div className="flex items-center gap-2 text-zinc-500 text-xs py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading detailed traffic data...
                  </div>
                )}

                {detailedData && !isLoadingDetailed && (
                  <>
                    {/* User-based Summary */}
                    <div className="bg-zinc-900 rounded p-3">
                      <h4 className="text-xs text-zinc-400 font-mono mb-2 flex items-center gap-1">
                        <Users className="w-3 h-3" /> User Summary (Last 30 Days)
                      </h4>
                      <div className="grid grid-cols-4 gap-2">
                        <div className="text-center">
                          <p className="text-lg font-bold text-white">{detailedData.summary?.last_30?.total_users?.toLocaleString() || 0}</p>
                          <p className="text-[10px] text-zinc-500">Total Users</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-cyan-400">{detailedData.summary?.last_30?.active_users?.toLocaleString() || 0}</p>
                          <p className="text-[10px] text-zinc-500">Active Users</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-green-400">{detailedData.summary?.last_30?.new_users?.toLocaleString() || 0}</p>
                          <p className="text-[10px] text-zinc-500">New Users</p>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {getTrendIcon(detailedData.user_trend_direction)}
                            <span className={`text-lg font-bold ${getTrendColor(detailedData.user_trend_direction)}`}>
                              {detailedData.user_trend_percent > 0 ? '+' : ''}{detailedData.user_trend_percent}%
                            </span>
                          </div>
                          <p className="text-[10px] text-zinc-500">User Trend</p>
                        </div>
                      </div>
                    </div>

                    {/* Traffic Sources by Users */}
                    {detailedData.traffic_sources?.length > 0 && (
                      <div className="bg-zinc-900 rounded p-3">
                        <h4 className="text-xs text-zinc-400 font-mono mb-2 flex items-center gap-1">
                          <Activity className="w-3 h-3" /> Traffic Sources (by Users)
                        </h4>
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {detailedData.traffic_sources.map((source, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-zinc-800 last:border-0">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-zinc-300 truncate">{source.source}</span>
                                <span className="text-zinc-600">/</span>
                                <span className="text-zinc-500 truncate">{source.medium}</span>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <span className="text-white font-mono">{source.total_users?.toLocaleString()}</span>
                                <span className="text-zinc-600 text-[10px]">users</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Country Breakdown */}
                    {detailedData.countries?.length > 0 && (
                      <div className="bg-zinc-900 rounded p-3">
                        <h4 className="text-xs text-zinc-400 font-mono mb-2 flex items-center gap-1">
                          <Globe className="w-3 h-3" /> Countries (by Users)
                        </h4>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {detailedData.countries.slice(0, 10).map((country, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-zinc-800 last:border-0">
                              <span className="text-zinc-300">{country.country}</span>
                              <span className="text-white font-mono">{country.total_users?.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* City Breakdown */}
                    {detailedData.cities?.length > 0 && (
                      <div className="bg-zinc-900 rounded p-3">
                        <h4 className="text-xs text-zinc-400 font-mono mb-2">Top Cities (by Users)</h4>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {detailedData.cities.slice(0, 10).map((city, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs py-1 border-b border-zinc-800 last:border-0">
                              <div>
                                <span className="text-zinc-300">{city.city}</span>
                                <span className="text-zinc-600 ml-1">({city.country})</span>
                              </div>
                              <span className="text-white font-mono">{city.total_users?.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {!trafficData && !isFetching && (
        <p className="text-zinc-600 text-xs">Click refresh to load organic traffic data</p>
      )}
    </div>
  );
};

// Connection card with properties
const ConnectionCard = ({ connection, onDiscover, onDelete, onFetchTraffic, onFetchAllTraffic, trafficData, isDiscovering, fetchingPropertyId, fetchAllProgress, onToggleHidden, onUpdateGroups, groups }) => {
  const [showProperties, setShowProperties] = useState(true);
  const [propertySearch, setPropertySearch] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [filterGroup, setFilterGroup] = useState('all');

  // Filter properties based on search, visibility, and group
  const filteredProperties = connection.properties?.filter(prop => {
    // Visibility filter
    if (!showHidden && prop.hidden) return false;
    
    // Group filter
    if (filterGroup !== 'all') {
      if (!prop.groups || !prop.groups.includes(filterGroup)) return false;
    }
    
    // Search filter
    if (propertySearch) {
      return prop.name.toLowerCase().includes(propertySearch.toLowerCase()) ||
        prop.account?.toLowerCase().includes(propertySearch.toLowerCase()) ||
        prop.id?.toLowerCase().includes(propertySearch.toLowerCase());
    }
    return true;
  }) || [];

  const hiddenCount = connection.properties?.filter(p => p.hidden).length || 0;
  const visibleProperties = connection.properties?.filter(p => !p.hidden) || [];
  const loadedCount = visibleProperties.filter(p => trafficData[`${connection.id}_${p.id}`]).length;
  const isFetchingAll = fetchAllProgress?.connectionId === connection.id;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-testid={`analytics-connection-${connection.id}`}>
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold">{connection.label}</h3>
              <div className="flex items-center gap-2">
                <Mail className="w-3 h-3 text-zinc-500" />
                <span className="text-zinc-500 text-sm">{connection.email}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-green-400">
            <CheckCircle2 className="w-4 h-4" />
            Connected
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 py-3 bg-zinc-800/30 flex items-center gap-2 flex-wrap">
        <button
          onClick={() => onDiscover(connection.id)}
          disabled={isDiscovering}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-mono bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50"
        >
          {isDiscovering ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          {connection.properties?.length ? 'Refresh Properties' : 'Discover Properties'}
        </button>
        
        {/* Fetch All Button */}
        {connection.properties?.length > 0 && (
          <button
            onClick={() => onFetchAllTraffic(connection.id)}
            disabled={isFetchingAll}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-mono bg-green-600 hover:bg-green-700 text-white rounded transition-colors disabled:opacity-50"
            data-testid="fetch-all-btn"
          >
            {isFetchingAll ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Fetching {fetchAllProgress?.current}/{fetchAllProgress?.total}...
              </>
            ) : (
              <>
                <Activity className="w-4 h-4" />
                Fetch All ({loadedCount}/{visibleProperties.length})
              </>
            )}
          </button>
        )}
        
        <button
          onClick={() => onDelete(connection.id)}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-mono text-red-400 hover:text-red-300 hover:bg-red-950/30 rounded transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Remove
        </button>
      </div>

      {/* Properties */}
      {connection.properties && connection.properties.length > 0 && (
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setShowProperties(!showProperties)}
              className="flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-300"
            >
              {showProperties ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {connection.properties.length} GA4 Properties
            </button>
          </div>

          {showProperties && (
            <>
              {/* Search and Filters */}
              <div className="space-y-3 mb-4">
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <input
                    type="text"
                    placeholder="Search properties..."
                    value={propertySearch}
                    onChange={(e) => setPropertySearch(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-zinc-100 text-sm placeholder-zinc-500 focus:outline-none focus:border-blue-500"
                    data-testid="property-search-input"
                  />
                  {propertySearch && (
                    <button
                      onClick={() => setPropertySearch('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      ×
                    </button>
                  )}
                </div>

                {/* Filter row */}
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Group filter */}
                  {groups && groups.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Filter className="w-3 h-3 text-zinc-500" />
                      <select
                        value={filterGroup}
                        onChange={(e) => setFilterGroup(e.target.value)}
                        className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-300 font-mono focus:outline-none focus:border-blue-500"
                        data-testid="ga4-group-filter"
                      >
                        <option value="all">All Groups</option>
                        {groups.map(group => (
                          <option key={group.id} value={group.id}>{group.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Show hidden toggle */}
                  {hiddenCount > 0 && (
                    <button
                      onClick={() => setShowHidden(!showHidden)}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-mono transition-colors ${
                        showHidden 
                          ? "bg-zinc-700 text-white" 
                          : "bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800"
                      }`}
                    >
                      {showHidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {showHidden ? `Hide ${hiddenCount} hidden` : `Show ${hiddenCount} hidden`}
                    </button>
                  )}
                </div>
              </div>

              {/* Filtered count */}
              <p className="text-xs text-zinc-500 mb-3">
                Showing {filteredProperties.length} of {connection.properties.length - (showHidden ? 0 : hiddenCount)} properties
              </p>

              {/* Properties List */}
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {filteredProperties.length > 0 ? (
                  filteredProperties.map((prop, index) => (
                    <PropertyCard
                      key={prop.id}
                      property={prop}
                      connectionId={connection.id}
                      onFetchData={(propId) => onFetchTraffic(connection.id, propId)}
                      trafficData={trafficData[`${connection.id}_${prop.id}`]}
                      isFetching={fetchingPropertyId === `${connection.id}_${prop.id}`}
                      autoFetch={!prop.hidden && index < 10}
                      autoFetchDelay={index * 1000}
                      onToggleHidden={onToggleHidden}
                      onUpdateGroups={onUpdateGroups}
                      groups={groups}
                    />
                  ))
                ) : (
                  <p className="text-zinc-500 text-sm text-center py-4">
                    No properties match your filters
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* No properties message */}
      {(!connection.properties || connection.properties.length === 0) && (
        <div className="p-4 text-center">
          <p className="text-zinc-500 text-sm">
            Click "Discover Properties" to find your GA4 properties
          </p>
        </div>
      )}

      {/* Last refresh */}
      {connection.last_refresh && (
        <div className="px-4 py-2 border-t border-zinc-800">
          <p className="text-zinc-600 text-xs">
            Properties refreshed: {new Date(connection.last_refresh).toLocaleString()}
          </p>
        </div>
      )}
    </div>
  );
};

// Main Analytics Component
export function Analytics() {
  const [loading, setLoading] = useState(true);
  const [connections, setConnections] = useState([]);
  const [status, setStatus] = useState({ configured: false });
  const [discoveringId, setDiscoveringId] = useState(null);
  const [fetchingPropertyId, setFetchingPropertyId] = useState(null);
  const [trafficData, setTrafficData] = useState({});
  const [connectingLabel, setConnectingLabel] = useState('');
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [groups, setGroups] = useState([]);
  const [fetchAllProgress, setFetchAllProgress] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/analytics/status`);
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (e) {
      console.error("Failed to fetch analytics status");
    }
  }, []);

  const fetchConnections = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/analytics/connections`);
      if (res.ok) {
        const data = await res.json();
        setConnections(data.connections || []);
      }
    } catch (e) {
      console.error("Failed to fetch connections");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/groups`);
      if (res.ok) {
        const data = await res.json();
        setGroups(data);
      }
    } catch (e) {
      console.error("Failed to fetch groups");
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchConnections();
    fetchGroups();

    // Check URL for callback
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('analytics_connected');
    const error = params.get('analytics_error');

    if (connected) {
      toast.success("Google Analytics account connected!");
      window.history.replaceState({}, '', window.location.pathname);
      fetchConnections();
    }
    if (error) {
      toast.error(`Connection failed: ${error}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchStatus, fetchConnections, fetchGroups]);

  const togglePropertyHidden = async (connectionId, propertyId, hidden) => {
    try {
      const res = await fetch(`${API_URL}/api/analytics/connections/${connectionId}/properties/${encodeURIComponent(propertyId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hidden })
      });
      if (res.ok) {
        // Update local state
        setConnections(prev => prev.map(conn => {
          if (conn.id === connectionId) {
            return {
              ...conn,
              properties: conn.properties.map(p => 
                p.id === propertyId ? { ...p, hidden } : p
              )
            };
          }
          return conn;
        }));
        toast.success(hidden ? "Property hidden" : "Property shown");
      }
    } catch (e) {
      toast.error("Failed to update property");
    }
  };

  const updatePropertyGroups = async (connectionId, propertyId, newGroups) => {
    try {
      const res = await fetch(`${API_URL}/api/analytics/connections/${connectionId}/properties/${encodeURIComponent(propertyId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ groups: newGroups })
      });
      if (res.ok) {
        // Update local state
        setConnections(prev => prev.map(conn => {
          if (conn.id === connectionId) {
            return {
              ...conn,
              properties: conn.properties.map(p => 
                p.id === propertyId ? { ...p, groups: newGroups } : p
              )
            };
          }
          return conn;
        }));
        toast.success("Property groups updated");
      }
    } catch (e) {
      toast.error("Failed to update property groups");
    }
  };

  const startAuth = async () => {
    if (!connectingLabel.trim()) {
      toast.error("Please enter a label for this connection");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/analytics/auth/start?account_label=${encodeURIComponent(connectingLabel)}`);
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.auth_url;
      } else {
        toast.error("Failed to start authentication");
      }
    } catch (e) {
      toast.error("Failed to start authentication");
    }
  };

  const discoverProperties = async (connectionId) => {
    setDiscoveringId(connectionId);
    try {
      const res = await fetch(`${API_URL}/api/analytics/connections/${connectionId}/discover`, {
        method: 'POST'
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Found ${data.count} GA4 properties`);
        fetchConnections();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to discover properties");
      }
    } catch (e) {
      toast.error("Failed to discover properties");
    } finally {
      setDiscoveringId(null);
    }
  };

  const deleteConnection = async (connectionId) => {
    if (!window.confirm("Delete this connection and all cached data?")) return;

    try {
      const res = await fetch(`${API_URL}/api/analytics/connections/${connectionId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success("Connection deleted");
        fetchConnections();
        // Clear cached traffic data
        setTrafficData(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(key => {
            if (key.startsWith(connectionId)) {
              delete updated[key];
            }
          });
          return updated;
        });
      } else {
        toast.error("Failed to delete connection");
      }
    } catch (e) {
      toast.error("Failed to delete connection");
    }
  };

  const fetchTraffic = async (connectionId, propertyId) => {
    const key = `${connectionId}_${propertyId}`;
    setFetchingPropertyId(key);

    try {
      const res = await fetch(`${API_URL}/api/analytics/connections/${connectionId}/organic-traffic?property_id=${encodeURIComponent(propertyId)}`);
      if (res.ok) {
        const data = await res.json();
        setTrafficData(prev => ({
          ...prev,
          [key]: data
        }));
        toast.success("Organic traffic data loaded");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to fetch traffic data");
      }
    } catch (e) {
      toast.error("Failed to fetch traffic data");
    } finally {
      setFetchingPropertyId(null);
    }
  };

  const fetchAllTraffic = async (connectionId) => {
    const connection = connections.find(c => c.id === connectionId);
    if (!connection || !connection.properties) return;

    // Get visible (non-hidden) properties that don't have data yet
    const propertiesToFetch = connection.properties.filter(p => {
      if (p.hidden) return false;
      const key = `${connectionId}_${p.id}`;
      return !trafficData[key];
    });

    if (propertiesToFetch.length === 0) {
      toast.info("All properties already loaded");
      return;
    }

    setFetchAllProgress({ connectionId, current: 0, total: propertiesToFetch.length });
    toast.info(`Fetching data for ${propertiesToFetch.length} properties...`);

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < propertiesToFetch.length; i++) {
      const prop = propertiesToFetch[i];
      const key = `${connectionId}_${prop.id}`;
      
      setFetchAllProgress({ connectionId, current: i + 1, total: propertiesToFetch.length });

      try {
        const res = await fetch(`${API_URL}/api/analytics/connections/${connectionId}/organic-traffic?property_id=${encodeURIComponent(prop.id)}`);
        if (res.ok) {
          const data = await res.json();
          setTrafficData(prev => ({
            ...prev,
            [key]: data
          }));
          successCount++;
        } else {
          errorCount++;
        }
      } catch (e) {
        errorCount++;
      }

      // Rate limiting: wait 500ms between requests
      if (i < propertiesToFetch.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    setFetchAllProgress(null);
    
    if (errorCount === 0) {
      toast.success(`Loaded data for all ${successCount} properties`);
    } else {
      toast.warning(`Loaded ${successCount} properties, ${errorCount} failed`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-blue-400" />
            GA4 Organic Traffic
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Monitor organic search traffic across all your GA4 properties
          </p>
        </div>

        <button
          onClick={() => setShowConnectForm(!showConnectForm)}
          disabled={!status.configured}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-mono text-sm transition-colors disabled:opacity-50"
          data-testid="connect-analytics-btn"
        >
          <Plus className="w-4 h-4" />
          Connect Account
        </button>
      </div>

      {/* Connect Form */}
      {showConnectForm && status.configured && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
          <h3 className="text-white font-medium mb-3">Connect Google Account</h3>
          <p className="text-zinc-500 text-sm mb-4">
            Connect multiple Google accounts to monitor all your GA4 properties in one place.
          </p>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Label (e.g., 'Client A', 'Personal Account')"
              value={connectingLabel}
              onChange={(e) => setConnectingLabel(e.target.value)}
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-zinc-100 font-mono text-sm placeholder-zinc-600 focus:outline-none focus:border-blue-500"
              data-testid="account-label-input"
            />
            <button
              onClick={startAuth}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-mono text-sm transition-colors"
            >
              Connect
            </button>
            <button
              onClick={() => {
                setShowConnectForm(false);
                setConnectingLabel('');
              }}
              className="px-4 py-2 text-zinc-400 hover:text-white rounded-lg font-mono text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Not Configured Warning */}
      {!status.configured && (
        <div className="bg-yellow-950/30 border border-yellow-700/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
            <div>
              <p className="text-yellow-400 font-medium">Configuration Required</p>
              <p className="text-yellow-400/70 text-sm mt-1">
                Google OAuth credentials not configured. Please set GOOGLE_CLIENT_ID, 
                GOOGLE_CLIENT_SECRET, and update GOOGLE_REDIRECT_URI to include /api/analytics/auth/callback
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-zinc-800/30 border border-zinc-700 rounded-lg p-4">
        <p className="text-zinc-400 text-sm">
          <strong className="text-zinc-300">Multiple Account Support:</strong> Connect as many Google accounts as needed. 
          Each connection can access all GA4 properties under that account. Data is 
          <span className="text-blue-400 font-medium"> cached for 24 hours </span>
          to minimize API calls.
        </p>
      </div>

      {/* Connections */}
      {connections.length === 0 ? (
        <div className="text-center py-16 bg-zinc-900 rounded-lg border border-zinc-800">
          <BarChart3 className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-lg text-zinc-300 font-medium">No Connections</h3>
          <p className="text-zinc-500 text-sm mt-1 mb-4">
            Connect your Google accounts to start monitoring GA4 organic traffic
          </p>
          {status.configured && (
            <button 
              onClick={() => setShowConnectForm(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-mono text-sm"
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Connect Account
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {connections.map(conn => (
            <ConnectionCard
              key={conn.id}
              connection={conn}
              onDiscover={discoverProperties}
              onDelete={deleteConnection}
              onFetchTraffic={fetchTraffic}
              onFetchAllTraffic={fetchAllTraffic}
              trafficData={trafficData}
              isDiscovering={discoveringId === conn.id}
              fetchingPropertyId={fetchingPropertyId}
              fetchAllProgress={fetchAllProgress}
              onToggleHidden={(propId, hidden) => togglePropertyHidden(conn.id, propId, hidden)}
              onUpdateGroups={(propId, newGroups) => updatePropertyGroups(conn.id, propId, newGroups)}
              groups={groups}
            />
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {Object.keys(trafficData).length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-green-400" />
            Overall Summary
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-zinc-800 rounded-lg p-4 text-center">
              <Users className="w-6 h-6 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">
                {Object.values(trafficData).reduce((sum, d) => sum + (d.last_30_days_sessions || d.this_month_sessions || 0), 0).toLocaleString()}
              </p>
              <p className="text-xs text-zinc-500">Total Organic Sessions (Last 30 Days)</p>
            </div>
            <div className="bg-zinc-800 rounded-lg p-4 text-center">
              <Globe className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">
                {Object.keys(trafficData).length}
              </p>
              <p className="text-xs text-zinc-500">Properties Monitored</p>
            </div>
            <div className="bg-zinc-800 rounded-lg p-4 text-center">
              <UserPlus className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">
                {connections.length}
              </p>
              <p className="text-xs text-zinc-500">Connected Accounts</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Analytics;
